import { ExtractComponentProp, FormValues } from '@approvalmax/types';
import { InfoCircleIcon, SwingIcon } from '@approvalmax/ui';
import { Box, Button, Flex, Form, NumberField, Select, Text, Tooltip } from '@approvalmax/ui/src/components';
import { zodResolver } from '@hookform/resolvers/zod';
import { constants } from 'modules/common';
import { domain } from 'modules/data';
import { memo } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { basedOnLocationTax, discountTypeReference } from './QBooksTaxSwitcher.constants';
import { useDiscount, useQbooksInvoiceRequest, useSwitcher, useTaxRates } from './QBooksTaxSwitcher.hooks';
import { messages } from './QBooksTaxSwitcher.messages';
import { schema } from './QBooksTaxSwitcher.schema';
import { GridReversable } from './QBooksTaxSwitcher.styles';

const { DISCOUNT_MIN, DISCOUNT_PERCENT_MAX, DISCOUNT_VALUE_MAX } = constants.qBooksConstants;

export const QBooksTaxSwitcher = memo(() => {
    const request = useQbooksInvoiceRequest();

    const { isUSCompany, isDiscountEnabled, handleSwitch } = useSwitcher();
    const { taxRates, handleChangeTaxRate } = useTaxRates();
    const { handleChangeDiscountType, handleChangeDiscount } = useDiscount();

    const { details } = request;

    const form = useForm<z.infer<typeof schema>>({
        values: {
            taxRate: details.useAutoTaxes ? basedOnLocationTax.id : details.taxCode?.id ?? null,
            discount: details.discount ?? null,
            discountType: details.discountType,
        },
        resolver: zodResolver(schema),
        mode: 'onChange',
    });

    if (!isUSCompany && !isDiscountEnabled) {
        return null;
    }

    const handleFormChange: ExtractComponentProp<typeof Form<FormValues<typeof form>>, 'onChange'> = (
        values,
        fieldName
    ) => {
        switch (fieldName) {
            case 'taxRate': {
                handleChangeTaxRate(values.taxRate);

                break;
            }
            case 'discountType': {
                handleChangeDiscountType(values.discountType);

                break;
            }
            case 'discount': {
                handleChangeDiscount(values.discount);

                break;
            }
        }
    };

    return (
        <Form form={form} onChange={handleFormChange}>
            <Flex alignItems='center' spacing='0 12'>
                {isUSCompany && isDiscountEnabled && (
                    <Button variant='text' color='blue100' onClick={handleSwitch}>
                        <SwingIcon />
                    </Button>
                )}

                <GridReversable
                    gridTemplateColumns='max-content minmax(268px, auto)'
                    rowGap={12}
                    columnGap={8}
                    alignItems='center'
                    $reverse={details.applyTaxAfterDiscount}
                >
                    {isUSCompany && (
                        <>
                            {details.useAutoTaxes ? (
                                <Tooltip
                                    width={300}
                                    placement='top'
                                    activator={
                                        <Flex spacing='4' alignItems='center'>
                                            <Text font='label' fontSize='small' color='midnight70'>
                                                {messages.taxRateLabel}
                                            </Text>

                                            <InfoCircleIcon width={12} height={12} color='midnight70' />
                                        </Flex>
                                    }
                                >
                                    {messages.basedOnLocationTooltip}
                                </Tooltip>
                            ) : (
                                <Text font='label' fontSize='small' color='midnight70'>
                                    {messages.taxRateLabel}
                                </Text>
                            )}

                            <Box width='100%'>
                                <Select
                                    name='taxRate'
                                    size='small'
                                    itemIdKey='id'
                                    itemNameKey='text'
                                    items={taxRates}
                                    placeholder={messages.taxRatePlaceholder}
                                />
                            </Box>
                        </>
                    )}

                    {isDiscountEnabled && (
                        <>
                            <Text font='label' fontSize='small' color='midnight70'>
                                {messages.discountLabel}
                            </Text>

                            <Flex wrap={false} spacing='8'>
                                <Box width={200}>
                                    <Select
                                        name='discountType'
                                        size='small'
                                        items={discountTypeReference}
                                        itemIdKey='id'
                                        itemNameKey='text'
                                        clearable={false}
                                    />
                                </Box>

                                <Box width={60}>
                                    <NumberField
                                        name='discount'
                                        size='small'
                                        placeholder={messages.discountPlaceholder}
                                        precision={2}
                                        clearable={false}
                                        min={DISCOUNT_MIN}
                                        max={
                                            details.discountType === domain.QBooksDiscountType.Percent
                                                ? DISCOUNT_PERCENT_MAX
                                                : DISCOUNT_VALUE_MAX
                                        }
                                    />
                                </Box>
                            </Flex>
                        </>
                    )}
                </GridReversable>
            </Flex>
        </Form>
    );
});

QBooksTaxSwitcher.displayName = 'QBooksTaxSwitcher';
